<template>
  <base-panel
    :hide-print-button="false"
    :asset="asset"
    :asset-general-info="assetGeneralInfo"
    :asset-module="assetModule"
    :hide-tabs="true"
    :last-update="lastUpdate"
    :notifications="notifications"
  >
    <div class="oscillography-analysis">
      <oscillography-analysis-filters
        v-if="shouldShowFilters()"
        :loading="diagnosticsLoading"
        :selected-oscillograph="selectedOscillograph"
        :selected-maneuver-type="selectedManeuverType"
        :maneuver-type-disabled="isManeuverTypeDisabled()"
        :selected-module-phase="selectedModulePhase"
        :oscillographs-options="oscillographsOptions"
        :maneuver-type-options="maneuverTypeOptions"
        :module-phase-options="modulePhaseOptions"
        @update:selectedOscillograph="selectedOscillograph = $event"
        @update:selectedManeuverType="selectedManeuverType = $event"
        @update:selectedModulePhase="selectedModulePhase = $event"
      />

      <b-tabs
        v-model="activeTabIndex"
        class="oscillography-analysis__tabs"
      >
        <b-tab
          :title="tabs[0].title"
        >
          <oscillography-analysis-diagnostics-tab
            :key="diagnosticsTabRenderKey"
            :diagnostics-mode="diagnosticsMode"
            :diagnostics-mode-options="diagnosticsModeOptions"
            :loading="diagnosticsLoading"
            :report-data="diagnosticsReportData"
            :show-exceeded-points="showExceededPoints"
            :maneuver-type="selectedManeuverType"
            :chart-filters="diagnosticsChartFilters"
            @update:diagnosticsMode="setDiagnosticsMode"
            @update:showExceededPoints="setShowExceededPoints"
            @refresh-report="refreshDiagnosticsReport"
            @open-history="openOscillographyHistory"
          />
        </b-tab>
        <b-tab
          :title="tabs[1].title"
        >
          <oscillography-analysis-alarms-tab
            :alarm-mode="alarmMode"
            :alarm-mode-options="alarmModeOptions"
            :loading="alarmLoading"
            :phase="selectedModulePhaseName"
            :rows="alarmRows"
            @update:alarmMode="setAlarmMode"
            @open-history="openAlarmHistory"
          />
        </b-tab>
        <b-tab
          v-if="canAccessParameters"
          :title="tabs[2].title"
          class="oscillography-analysis__parameters-pane"
        >
          <oscillography-analysis-parameters-tab
            :asset-module-active-ids="diagnosticModuleActiveIds"
          />
        </b-tab>
      </b-tabs>
    </div>
  </base-panel>
</template>

<script>
import BasePanel from '@/domain/components/installation/engineering/BasePanel';
import MAX_MANEUVER_LIMIT from '@/domain/constants/reports/oscillography';
import eventBus from '@/services/globalEventBus';
import engineeringEvents from '@/domain/events/engineering';
import OscillographyAnalysisService, {
  OSCILLOGRAPHY_ANALYSIS_MODULE_ID,
} from '@/domain/services/installation/engineering/oscillographyAnalysis/oscillographyAnalysis';
import OscillographyAnalysisFilters from './OscillographyAnalysisFilters';
import OscillographyAnalysisDiagnosticsTab from './OscillographyAnalysisDiagnosticsTab';
import OscillographyAnalysisAlarmsTab from './OscillographyAnalysisAlarmsTab';
import OscillographyAnalysisParametersTab from './OscillographyAnalysisParametersTab';

const tabNames = {
  DIAGNOSTICS: 'diagnostics',
  ALARMS: 'alarms',
  DIAGNOSTIC_PARAMETERS: 'diagnostic-parameters',
};

const emptyDiagnosticsReport = () => ({
  chartData: [],
  diagnosticsChartData: [],
  maneuvers: [],
});

export default {
  name: 'OscillographyAnalysis',
  components: {
    BasePanel,
    OscillographyAnalysisFilters,
    OscillographyAnalysisDiagnosticsTab,
    OscillographyAnalysisAlarmsTab,
    OscillographyAnalysisParametersTab,
  },
  props: {
    asset: {
      type: Object,
      default: () => ({}),
    },
    assetType: {
      type: Object,
      default: () => ({}),
    },
    assetModule: {
      type: Object,
      default: () => ({}),
    },
    assetGeneralInfo: {
      type: Object,
      default: () => ({}),
    },
    notifications: {
      type: Array,
      default: () => [],
    },
  },
  data: () => ({
    activeTabIndex: 0,
    diagnosticsMode: 'graphic',
    diagnosticsTabRenderKey: 0,
    alarmMode: 'breaker',
    alarmRows: [],
    alarmLoading: false,
    alarmRequestId: 0,
    diagnosticsRequestId: 0,
    diagnosticsReportData: emptyDiagnosticsReport(),
    diagnosticsReportId: null,
    diagnosticsLoading: false,
    diagnosticsChartFilters: {
      multiAxis: true,
      removeOutlier: false,
    },
    oscillographyModuleIds: [],
    oscillographyPhases: [],
    lastUpdate: '',
    selectedOscillograph: 'last-10',
    selectedManeuverType: 'opening',
    selectedModulePhase: null,
    showExceededPoints: true,
    tabNames,
  }),
  computed: {
    currentInstallationName() {
      return this.$store?.getters?.installation?.name || '';
    },
    canAccessParameters() {
      return this.$hasUserPermission('PER033');
    },
    tabs() {
      return [
        {
          name: this.tabNames.DIAGNOSTICS,
          title: this.$t('engineering.oscillography-analysis.tabs.diagnostics'),
        },
        {
          name: this.tabNames.ALARMS,
          title: this.$t('engineering.oscillography-analysis.tabs.alarms'),
        },
        {
          name: this.tabNames.DIAGNOSTIC_PARAMETERS,
          title: this.$t('engineering.oscillography-analysis.tabs.diagnostic-parameters'),
        },
      ];
    },
    oscillographsOptions() {
      return [
        {
          text: this.$t('engineering.oscillography-analysis.filters.oscillographs-options.last-10'),
          value: 'last-10',
        },
        {
          text: this.$t('engineering.oscillography-analysis.filters.oscillographs-options.last-15'),
          value: 'last-15',
        },
        {
          text: this.$t('engineering.oscillography-analysis.filters.oscillographs-options.last-20'),
          value: 'last-20',
        },
      ];
    },
    maneuverTypeOptions() {
      return [
        {
          text: this.$t('engineering.oscillography-analysis.filters.maneuver-type-options.opening'),
          value: 'opening',
        },
        {
          text: this.$t('engineering.oscillography-analysis.filters.maneuver-type-options.closing'),
          value: 'closing',
        },
        {
          text: this.$t('engineering.oscillography-analysis.filters.maneuver-type-options.motor'),
          value: 'engine',
        },
      ];
    },
    modulePhaseOptions() {
      return [
        {
          text: this.$t('engineering.oscillography-analysis.filters.module-phase-options.all'),
          value: null,
        },
        ...this.oscillographyPhases.map(module => ({
          text: module.name,
          value: module.id,
        })),
      ];
    },
    selectedModulePhaseName() {
      return this.modulePhaseOptions.find(option => option.value === this.selectedModulePhase)?.text || '';
    },
    diagnosticModuleActiveIds() {
      return this.assetModule?.id ? [this.assetModule.id] : [];
    },
    diagnosticsModeOptions() {
      return [
        {
          text: this.$t('engineering.oscillography-analysis.diagnostics.graphic'),
          value: 'graphic',
        },
        {
          text: this.$t('engineering.oscillography-analysis.diagnostics.information'),
          value: 'information',
        },
      ];
    },
    alarmModeOptions() {
      return [
        {
          text: this.$t('engineering.oscillography-analysis.alarms.breaker'),
          value: 'breaker',
        },
        {
          text: this.$t('engineering.oscillography-analysis.alarms.motor'),
          value: 'motor',
        },
      ];
    },
  },
  watch: {
    activeTabIndex(activeTabIndex) {
      if (activeTabIndex === 0) {
        this.diagnosticsTabRenderKey += 1;
        this.loadDiagnosticsReport();
      } else if (activeTabIndex === 1) {
        this.loadAlarmData();
      }
    },
    selectedOscillograph() {
      this.loadActiveTabData();
    },
    selectedManeuverType() {
      if (this.activeTabIndex === 0) {
        this.diagnosticsTabRenderKey += 1;
        this.loadDiagnosticsReport();
      }
    },
    selectedModulePhase() {
      this.loadActiveTabData();
    },
    alarmMode() {
      if (this.activeTabIndex === 1) {
        this.loadAlarmData();
      }
    },
  },
  async created() {
    this.initEvents();
    await this.loadStatus();

    await this.loadOscillographyModules();
    await Promise.all([
      this.loadDiagnosticsReport(),
      this.loadAlarmData(),
    ]);
  },
  beforeDestroy() {
    this.removeEvents();
  },
  methods: {
    initEvents() {
      eventBus.on(engineeringEvents.refreshData, this.refreshData, this);
    },
    removeEvents() {
      eventBus.off(engineeringEvents.refreshData, this.refreshData, this);
    },
    refreshData({ wait }) {
      wait(this.refreshAnalysisData());
    },
    async refreshAnalysisData() {
      await this.loadStatus();
      await this.loadOscillographyModules();
      await Promise.all([
        this.loadDiagnosticsReport(),
        this.loadAlarmData(),
      ]);
    },
    async loadStatus() {
      if (!this.assetModule?.id) {
        this.lastUpdate = '';
        return;
      }

      try {
        const status = await OscillographyAnalysisService.getStatus(this.assetModule.id);
        this.lastUpdate = status?.lastUpdate || '';
      } catch (error) {
        this.lastUpdate = '';
      }
    },
    isManeuverTypeDisabled() {
      return this.activeTabIndex === 1;
    },
    shouldShowFilters() {
      return this.activeTabIndex !== 2;
    },
    loadActiveTabData() {
      if (this.activeTabIndex === 0) {
        this.diagnosticsTabRenderKey += 1;
        this.loadDiagnosticsReport();
      } else if (this.activeTabIndex === 1) {
        this.loadAlarmData();
      }
    },
    async loadOscillographyModules() {
      if (!this.asset?.id) {
        this.oscillographyModuleIds = [];
        this.oscillographyPhases = [];
        return;
      }

      const { reportModuleIds, phases } = await OscillographyAnalysisService.getModules(
        this.asset.id,
      );
      this.oscillographyModuleIds = reportModuleIds;
      this.oscillographyPhases = phases;
    },
    async loadDiagnosticsReport() {
      const requestId = this.diagnosticsRequestId + 1;
      this.diagnosticsRequestId = requestId;

      if (!this.asset?.id || !this.assetType?.id || !this.oscillographyModuleIds.length) {
        this.diagnosticsReportId = null;
        this.diagnosticsReportData = emptyDiagnosticsReport();
        this.diagnosticsLoading = false;
        return;
      }

      this.diagnosticsReportId = null;
      this.diagnosticsReportData = emptyDiagnosticsReport();
      this.diagnosticsLoading = true;

      try {
        const { reportId, reportData } = await OscillographyAnalysisService.getDiagnostics({
          assetId: this.asset.id,
          assetTypeId: this.assetType.id,
          installationId: this.$route.params.installationId,
          maneuverType: this.selectedManeuverType,
          moduleIds: this.oscillographyModuleIds,
          assetModuleId: this.selectedModulePhase,
          oscillographs: this.selectedOscillograph,
          chartFilters: {
            ...this.diagnosticsChartFilters,
            filterDiagnosticsByManeuver: true,
          },
        });

        if (requestId === this.diagnosticsRequestId) {
          this.diagnosticsReportId = reportId;
          this.diagnosticsReportData = reportData;
        }
      } catch (error) {
        if (requestId === this.diagnosticsRequestId) {
          this.diagnosticsReportId = null;
          this.diagnosticsReportData = emptyDiagnosticsReport();
        }
      } finally {
        if (requestId === this.diagnosticsRequestId) {
          this.diagnosticsLoading = false;
        }
      }
    },
    async refreshDiagnosticsReport(chartFilters = {}) {
      if (!this.diagnosticsReportId) {
        return;
      }

      const requestId = this.diagnosticsRequestId + 1;
      this.diagnosticsRequestId = requestId;
      const reportId = this.diagnosticsReportId;
      this.diagnosticsChartFilters = {
        ...this.diagnosticsChartFilters,
        ...chartFilters,
      };
      this.diagnosticsLoading = true;

      try {
        const reportData = await OscillographyAnalysisService.refreshDiagnostics(
          reportId,
          {
            ...this.diagnosticsChartFilters,
            filterDiagnosticsByManeuver: true,
          },
        );
        if (requestId === this.diagnosticsRequestId) {
          this.diagnosticsReportData = reportData;
        }
      } finally {
        if (requestId === this.diagnosticsRequestId) {
          this.diagnosticsLoading = false;
        }
      }
    },
    setDiagnosticsMode(mode) {
      this.diagnosticsMode = mode;
    },
    setAlarmMode(mode) {
      this.alarmMode = mode;
    },
    setShowExceededPoints(showExceededPoints) {
      this.showExceededPoints = showExceededPoints;
    },
    async loadAlarmData() {
      const requestId = this.alarmRequestId + 1;
      this.alarmRequestId = requestId;

      if (!this.assetModule?.id) {
        this.alarmRows = [];
        return;
      }

      this.alarmLoading = true;
      try {
        const alarmRows = await OscillographyAnalysisService.getAlarms({
          assetModuleId: this.assetModule.id,
          assetModuleIds: this.selectedModulePhase
            ? [this.selectedModulePhase]
            : this.oscillographyPhases.map(module => module.id),
          alarmMode: this.alarmMode,
          oscillographs: this.selectedOscillograph,
          phase: this.selectedModulePhaseName,
        });

        if (requestId === this.alarmRequestId) {
          this.alarmRows = alarmRows;
        }
      } catch (error) {
        if (requestId === this.alarmRequestId) {
          this.alarmRows = [];
        }
      } finally {
        if (requestId === this.alarmRequestId) {
          this.alarmLoading = false;
        }
      }
    },
    openOscillographyHistory() {
      const maneuverKeys = (this.diagnosticsReportData.maneuvers || [])
        .slice()
        .sort((first, second) => (
          new Date(second.dateTimeOscillography).getTime()
          - new Date(first.dateTimeOscillography).getTime()
        ))
        .filter(maneuver => (maneuver.maneuverId !== undefined
          && maneuver.maneuverId !== null)
          || (maneuver.oscillographyId !== undefined
            && maneuver.oscillographyId !== null))
        .map((maneuver) => {
          const [, assetModuleId = '', setGroup = ''] = (maneuver.maneuverKey || '').split(':');
          const maneuverId = maneuver.maneuverId ?? maneuver.oscillographyId;
          const moduleKey = assetModuleId || maneuver.assetModuleId || '';
          const setGroupKey = setGroup || maneuver.setGroup || '';
          return `${maneuverId}:${moduleKey}${setGroupKey ? `:${setGroupKey}` : ''}`;
        })
        .slice(0, MAX_MANEUVER_LIMIT);
      const historyContextKey = `sigma-oscillography-history-${Date.now()}-${Math.random()
        .toString(36).slice(2)}`;
      const historyContext = {
        installationId: this.$route.params.installationId,
        assetTypeId: this.assetType.id,
        assetId: this.asset.id,
        modules: this.oscillographyModuleIds,
        maneuverTypes: [this.selectedManeuverType],
        maneuverSubTypes: ['ordinary', 'learning', 'orphan'],
        maneuverKeys,
      };

      if (this.currentInstallationName) {
        historyContext.installationName = this.currentInstallationName;
      }

      localStorage.setItem(historyContextKey, JSON.stringify(historyContext));

      const routeData = this.$router.resolve({
        name: 'reports.oscillography.new-report',
        query: { historyContextKey },
      });

      return window.open(routeData.href, '_blank');
    },
    openAlarmHistory() {
      const installation = this.$store?.getters?.installation || {};
      const company = installation.companyId
        ? { id: installation.companyId, name: installation.companyName || '' }
        : null;
      const regional = installation.regionalId
        ? { id: installation.regionalId, name: installation.regionalName || '' }
        : null;
      const installationOption = {
        id: installation.id || this.$route.params.installationId,
        name: installation.name || this.currentInstallationName,
        ...(company ? { company } : {}),
        ...(regional ? { regional } : {}),
      };
      const assetOption = {
        id: this.asset.id,
        name: this.asset.name || '',
        installation: installationOption,
      };
      const historyContextKey = `sigma-alarm-history-${Date.now()}-${Math.random()
        .toString(36).slice(2)}`;
      const historyContext = {
        company,
        regional,
        installation: installationOption,
        asset: assetOption,
        modules: [OSCILLOGRAPHY_ANALYSIS_MODULE_ID],
      };

      localStorage.setItem(historyContextKey, JSON.stringify(historyContext));

      const routeData = this.$router.resolve({
        name: 'reports.alarms-and-warnings.new-report',
        query: { historyContextKey },
      });

      return window.open(routeData.href, '_blank');
    },
  },
};
</script>

<style lang="sass">
.oscillography-analysis
  &__filters
    margin-bottom: 1.5rem

  &__field
    margin-bottom: 0

  &__toggle
    margin-top: .75rem

  &__tabs
    ::v-deep .nav-tabs
      margin-bottom: 0
    ::v-deep .tab-content
      padding-top: 0

  &__parameters-pane
    padding-top: 0 !important

</style>
